import React, { useEffect, useRef, useState } from 'react';
import '../assets/home.css';
import '../assets/home-rwd.css';
import '../assets/homev2.css';
import * as THREE from 'three';

const HomeV2 = () => {
    const canvasRef = useRef(null);
    const [score, setScore] = useState(0);
    const [lives, setLives] = useState(3);
    const [gameStatus, setGameStatus] = useState('waiting'); // 'waiting', 'playing', 'won', 'lost'
    
    const gameRef = useRef({
        scene: null,
        camera: null,
        renderer: null,
        paddle: null,
        ball: null,
        blocks: [],
        gameStarted: false,
        gameOver: false,
        score: 0,
        lives: 3,
        ballSpeed: 0.05,
        ballDirection: new THREE.Vector3(0.5, -1, 0).normalize(),
        paddleSpeed: 0.2,
        keys: {
            left: false,
            right: false
        }
    });

    // Initialize and setup the 3D scene
    useEffect(() => {
        if (!canvasRef.current) return;
        
        const game = gameRef.current;
        const canvas = canvasRef.current;
        
        // Setup scene, camera, and renderer
        game.scene = new THREE.Scene();
        game.scene.background = new THREE.Color(0xf9fafb);
        
        game.camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
        game.camera.position.z = 15;
        
        game.renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
        game.renderer.setSize(window.innerWidth, window.innerHeight);
        
        // Add lights
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
        game.scene.add(ambientLight);
        
        const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
        directionalLight.position.set(0, 10, 10);
        game.scene.add(directionalLight);
        
        // Create paddle
        const paddleGeometry = new THREE.BoxGeometry(4, 0.5, 1);
        const paddleMaterial = new THREE.MeshPhongMaterial({ color: 0x000000 });
        game.paddle = new THREE.Mesh(paddleGeometry, paddleMaterial);
        game.paddle.position.y = -8;
        game.scene.add(game.paddle);
        
        // Create ball
        const ballGeometry = new THREE.SphereGeometry(0.3, 32, 32);
        const ballMaterial = new THREE.MeshPhongMaterial({ color: 0xFFF65E });
        game.ball = new THREE.Mesh(ballGeometry, ballMaterial);
        game.ball.position.y = -7.5;
        game.scene.add(game.ball);
        
        // Create LaunchCool logo blocks
        createLogoBlocks();
        
        // Handle window resize
        const handleResize = () => {
            game.camera.aspect = window.innerWidth / window.innerHeight;
            game.camera.updateProjectionMatrix();
            game.renderer.setSize(window.innerWidth, window.innerHeight);
        };
        
        // Handle keyboard controls
        const handleKeyDown = (e) => {
            if (e.key === 'ArrowLeft') game.keys.left = true;
            if (e.key === 'ArrowRight') game.keys.right = true;
            if (e.key === ' ' && !game.gameStarted && !game.gameOver) {
                game.gameStarted = true;
            }
            if (e.key === 'r' && game.gameOver) {
                resetGame();
            }
        };
        
        const handleKeyUp = (e) => {
            if (e.key === 'ArrowLeft') game.keys.left = false;
            if (e.key === 'ArrowRight') game.keys.right = false;
        };
        
        // Create blocks for "LaunchCool" logo
        function createLogoBlocks() {
            // Clear existing blocks
            game.blocks.forEach(block => game.scene.remove(block));
            game.blocks = [];
            
            // Configuration for the logo text blocks
            const letters = [
                // L
                [-8, 7], [-8, 6], [-8, 5], [-8, 4], [-7, 4], [-6, 4],
                // A
                [-5, 7], [-5, 6], [-5, 5], [-5, 4], [-4, 7], [-3, 7], [-3, 6], [-3, 5], [-3, 4], [-4, 5.5],
                // U
                [-2, 7], [-2, 6], [-2, 5], [-2, 4], [0, 7], [0, 6], [0, 5], [0, 4], [-1, 4],
                // N
                [1, 7], [1, 6], [1, 5], [1, 4], [3, 7], [3, 6], [3, 5], [3, 4], [2, 6], [2, 5],
                // C
                [4, 7], [4, 6], [4, 5], [4, 4], [5, 7], [6, 7], [5, 4], [6, 4],
                // H
                [7, 7], [7, 6], [7, 5], [7, 4], [9, 7], [9, 6], [9, 5], [9, 4], [8, 5.5],
                
                // C
                [-6, 2], [-6, 1], [-6, 0], [-6, -1], [-5, 2], [-4, 2], [-5, -1], [-4, -1],
                // O
                [-3, 2], [-3, 1], [-3, 0], [-3, -1], [-1, 2], [-1, 1], [-1, 0], [-1, -1], [-2, 2], [-2, -1],
                // O
                [0, 2], [0, 1], [0, 0], [0, -1], [2, 2], [2, 1], [2, 0], [2, -1], [1, 2], [1, -1],
                // L
                [3, 2], [3, 1], [3, 0], [3, -1], [4, -1], [5, -1]
            ];
            
            // Create blocks for each coordinate
            letters.forEach(([x, y]) => {
                const blockGeometry = new THREE.BoxGeometry(0.9, 0.9, 0.9);
                
                // Set different colors for blocks
                let blockColor;
                if (y > 3) { // "LAUNCH" 
                    blockColor = 0x009688;
                } else { // "COOL"
                    blockColor = 0xFFF65E;
                }
                
                const blockMaterial = new THREE.MeshPhongMaterial({ color: blockColor });
                const block = new THREE.Mesh(blockGeometry, blockMaterial);
                block.position.set(x, y, 0);
                
                game.blocks.push(block);
                game.scene.add(block);
            });
        }
        
        // Reset game state
        function resetGame() {
            game.gameStarted = false;
            game.gameOver = false;
            game.score = 0;
            game.lives = 3;
            game.ballSpeed = 0.05;
            game.ballDirection = new THREE.Vector3(0.5, -1, 0).normalize();
            
            game.ball.position.set(0, -7.5, 0);
            game.paddle.position.set(0, -8, 0);
            
            createLogoBlocks();
            
            // Update React state
            setScore(0);
            setLives(3);
            setGameStatus('waiting');
        }
        
        // Start the animation loop
        function animate() {
            requestAnimationFrame(animate);
            
            // Handle paddle movement
            if (game.keys.left && game.paddle.position.x > -10) {
                game.paddle.position.x -= game.paddleSpeed;
            }
            if (game.keys.right && game.paddle.position.x < 10) {
                game.paddle.position.x += game.paddleSpeed;
            }
            
            // Move the ball when game has started
            if (game.gameStarted && !game.gameOver) {
                game.ball.position.x += game.ballDirection.x * game.ballSpeed;
                game.ball.position.y += game.ballDirection.y * game.ballSpeed;
                
                // Ball and wall collision
                if (game.ball.position.x <= -11.5 || game.ball.position.x >= 11.5) {
                    game.ballDirection.x = -game.ballDirection.x;
                }
                
                if (game.ball.position.y >= 8.5) {
                    game.ballDirection.y = -game.ballDirection.y;
                }
                
                // Ball falls out of bounds
                if (game.ball.position.y < -10) {
                    game.lives--;
                    if (game.lives <= 0) {
                        game.gameOver = true;
                    } else {
                        game.gameStarted = false;
                        game.ball.position.set(0, -7.5, 0);
                        game.ballDirection = new THREE.Vector3(0.5, -1, 0).normalize();
                    }
                }
                
                // Ball and paddle collision
                const paddleMinX = game.paddle.position.x - 2;
                const paddleMaxX = game.paddle.position.x + 2;
                const paddleTopY = game.paddle.position.y + 0.25;
                
                if (
                    game.ball.position.y - 0.3 <= paddleTopY &&
                    game.ball.position.y - 0.3 > paddleTopY - 0.3 &&
                    game.ball.position.x >= paddleMinX &&
                    game.ball.position.x <= paddleMaxX
                ) {
                    // Calculate reflection angle based on where the ball hits the paddle
                    const hitPosition = (game.ball.position.x - game.paddle.position.x) / 2;
                    game.ballDirection.x = hitPosition;
                    game.ballDirection.y = -game.ballDirection.y;
                    game.ballDirection.normalize();
                    
                    // Increase ball speed slightly
                    game.ballSpeed = Math.min(game.ballSpeed + 0.002, 0.15);
                }
                
                // Ball and block collision
                for (let i = game.blocks.length - 1; i >= 0; i--) {
                    const block = game.blocks[i];
                    const blockMinX = block.position.x - 0.45;
                    const blockMaxX = block.position.x + 0.45;
                    const blockMinY = block.position.y - 0.45;
                    const blockMaxY = block.position.y + 0.45;
                    
                    if (
                        game.ball.position.x + 0.3 >= blockMinX &&
                        game.ball.position.x - 0.3 <= blockMaxX &&
                        game.ball.position.y + 0.3 >= blockMinY &&
                        game.ball.position.y - 0.3 <= blockMaxY
                    ) {
                        // Remove the block
                        game.scene.remove(block);
                        game.blocks.splice(i, 1);
                        
                        // Increase score
                        game.score += 10;
                        
                        // Determine which side of the block was hit
                        const overlapLeft = game.ball.position.x + 0.3 - blockMinX;
                        const overlapRight = blockMaxX - (game.ball.position.x - 0.3);
                        const overlapTop = game.ball.position.y + 0.3 - blockMinY;
                        const overlapBottom = blockMaxY - (game.ball.position.y - 0.3);
                        
                        // Find the smallest overlap
                        const minOverlap = Math.min(overlapLeft, overlapRight, overlapTop, overlapBottom);
                        
                        // Reflect ball direction based on collision side
                        if (minOverlap === overlapLeft || minOverlap === overlapRight) {
                            game.ballDirection.x = -game.ballDirection.x;
                        } else {
                            game.ballDirection.y = -game.ballDirection.y;
                        }
                        
                        break;
                    }
                }
                
                // Win condition
                if (game.blocks.length === 0) {
                    // Player destroyed all blocks
                    game.gameOver = true;
                }
            }
            
            // Create text overlays for game status
            renderGameInfo();
            
            game.renderer.render(game.scene, game.camera);
        }
        
        // Render game information (score, lives, instructions)
        function renderGameInfo() {
            // Update React state instead of manipulating DOM
            setScore(game.score);
            setLives(game.lives);
            
            if (!game.gameStarted && !game.gameOver) {
                setGameStatus('waiting');
            } else if (game.gameOver) {
                if (game.blocks.length === 0) {
                    setGameStatus('won');
                } else {
                    setGameStatus('lost');
                }
            } else {
                setGameStatus('playing');
            }
        }
        
        // Add event listeners
        window.addEventListener('resize', handleResize);
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        
        // Start the animation loop
        animate();
        
        // Cleanup on unmount
        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
            
            if (game.renderer) {
                game.renderer.dispose();
            }
        };
    }, []);
    
    return (
        <div className="game-container">
            <canvas ref={canvasRef} className="game-canvas"></canvas>
            
            <div className="game-status">
                <div className="game-score">得分: {score}</div>
                <div className="game-lives">生命: {lives}</div>
            </div>
            
            {gameStatus === 'waiting' && (
                <div className="game-message game-start-message">
                    <div>按空格鍵開始遊戲</div>
                    <div className="game-sub-message">使用左右方向鍵移動擋板</div>
                </div>
            )}
            
            {gameStatus === 'won' && (
                <div className="game-message game-win-message">
                    <div>恭喜你贏了！</div>
                    <div className="game-sub-message">最終得分: {score}</div>
                    <div className="game-sub-message">按 R 鍵重新開始</div>
                </div>
            )}
            
            {gameStatus === 'lost' && (
                <div className="game-message game-over-message">
                    <div>遊戲結束</div>
                    <div className="game-sub-message">最終得分: {score}</div>
                    <div className="game-sub-message">按 R 鍵重新開始</div>
                </div>
            )}
            
            <div className="game-instructions">
                <h3>LaunchCool 3D打磚塊</h3>
                <ul>
                    <li>空格鍵開始遊戲</li>
                    <li>左右方向鍵移動擋板</li>
                    <li>打破所有「LaunchCool」磚塊得分</li>
                    <li>遊戲結束時按R鍵重新開始</li>
                </ul>
            </div>
            
            <div className="hero-section-overlay">
                <div className="container">
                    <div className="homeActionBtn">
                        <a href="/a" className="btn btn-cta">
                            <i className="fa-solid fa-users"></i>
                            <span> 尋找夥伴</span>
                        </a>
                        <a href="/cp" className="btn btn-cta-secondary">
                            <i className="icon-lightbulb fa-regular fa-lightbulb"></i>
                            <span> 發起點子</span>
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HomeV2; 